/**
 * NoteRecordOverview — the slideout overview for an ahoy_note record.
 *
 * Renders the SAME `<NoteCard>` used by the notes tab in other slideouts
 * (contact / company / deal). Re-using NoteCard keeps a single source of
 * truth for "how a note looks when displayed" — body preview, owner/creator
 * attribution, expand/collapse, edit/delete affordances, and the
 * edit-mode flip to `notes/MemoForm.tsx`.
 *
 * The slideout's standard OverviewSection (property field list + edit
 * toggle) is bypassed via an early branch in overview-section.tsx — see
 * the `case ahoy_note` there.
 */

import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';

import { useAuthInfo } from '@/allauth_auth/hooks';
import { useCurrentUserEntityId, useOrgMembers } from '@/hooks/useOrgMembers';
import { Skeleton, SkeletonText } from '@/lib/base/skeleton';
import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';
import type { CrmObject } from '@/types/crm';

import {
  getPendingCreationsSnapshot,
  subscribeToPendingCreations,
} from '../../store/crmObjectStore';
import { useRecordDraft } from '../contexts/RecordDraftContext';
import { useStoreObjectSubscription } from '../hooks/useStoreObjectSubscription';
import {
  getMemoCreatorName,
  getMemoOwnerName,
  resolveMemberName,
} from '../memo/memoMembers';
import { NoteLinkedRecordsSection } from '../memo/NoteLinkedRecordsSection';
import {
  extractMemoData,
  memoPrivacy,
  OWNER_ASSOCIATION_TYPE_NAME,
} from '../memo/notes.utils';
import { preloadPlateNoteEditor } from '../memo/preloadPlateNoteEditor';
import { useMemoPrivacyToggle, useMemoRecords } from '../memo/useMemoRecords';
import { NoteCard } from './NoteCard';

interface NoteRecordOverviewProps {
  /** The ahoy_note CrmObject backing the slideout. */
  object: CrmObject;
  /**
   * Edit mode driven by the slideout header's "Edit note" button (see
   * RecordViewContext, which owns this state and renders the header action for
   * overview overrides that opt in via `overviewHeaderEditLabel`). Threaded
   * straight through to NoteCard, which renders the edit form when true.
   */
  editMode?: boolean;
  onEditModeChange?: (isEditMode: boolean) => void;
}

export function NoteRecordOverview({
  object,
  editMode,
  onEditModeChange,
}: NoteRecordOverviewProps) {
  const orgId = object.org_id;

  const { user } = useAuthInfo();
  const userId = user?.id ? String(user.id) : undefined;

  // Editing IS the primary action on the plain-note record slideout, so warm
  // the lazy Plate.js editor chunk the moment the slideout opens. By the time
  // the user clicks into the note (or the header "Edit note" button) the chunk
  // is cached, so edit mode opens instantly instead of blocking ~1s on the
  // first dynamic import.
  useEffect(() => {
    preloadPlateNoteEditor();
  }, []);

  const { members: orgUsers } = useOrgMembers(orgId);
  const userEntityId = useCurrentUserEntityId(orgId);
  // Only for the prospective-creator name below — the viewer's OWN member row,
  // used to label a not-yet-created note. Distinct question from `userEntityId`.
  const currentMember = orgUsers.find(u => u.userId === user?.id);

  // The owner association_type_name is needed by extractMemoData to read the
  // ahoy_user target out of the note's association list. Match by the
  // canonical `ahoy_note_to_owner` name — there are multiple note→user
  // association types (owner, creator, visible_user, mentioned) and a
  // (source, target) match could pick the wrong one depending on list order.
  const { associationTypes } = useAssociationTypes(orgId);
  const ownerAssociationTypeName = useMemo(
    () =>
      associationTypes.find(
        at => at.association_type_name === OWNER_ASSOCIATION_TYPE_NAME
      )?.association_type_name ?? null,
    [associationTypes]
  );

  // No stage association type name is passed (3rd arg): the note family has no
  // stage/pipeline concept (getMemoAssociationNames('ahoy_note').stage is null —
  // epic #13640). This is NOT a dropped legacy stage: the update mutation guards
  // EVERY stage add/remove on `names.stage !== null` (see
  // useMemoRecords.updateMutation), so a legacy note's stage association is never
  // diffed or removed on edit regardless — it round-trips untouched. NoteCard
  // renders no stage at all, so there is nothing to surface.
  // Reading the retired ahoy_note stage here would only re-introduce the concept
  // the epic removed, with no behavioral effect. Tasks resolve a real stage —
  // see TaskRecordOverview.
  const note = useMemo(
    () => extractMemoData(object, ownerAssociationTypeName),
    [object, ownerAssociationTypeName]
  );

  // useMemoRecords is parametrised on a parent record for its LIST query, but we
  // only need the update mutations here. Passing entityId=undefined skips
  // the list fetch (the hook's `enabled` guard checks for it) so the
  // mutations work standalone. Delete now lives in the slideout header's
  // RecordActionMenu (which uses `useCrmObjectMutations` directly), so
  // this surface doesn't pull `deleteRecord` from useMemoRecords anymore.
  const { updateRecordAsync, isUpdating, updateError } = useMemoRecords({
    orgId,
    entityId: undefined,
    entityTypeName: '',
    users: orgUsers,
  });
  // Visibility saves through its own endpoint, with its own toasts and log —
  // shared with TaskRecordOverview so the two can't drift.
  const { togglePrivacy } = useMemoPrivacyToggle({
    orgId,
    recordType: 'ahoy_note',
    users: orgUsers,
  });

  const handleEdit = useCallback(
    (
      noteEntityId: string,
      title: string,
      content: string,
      opts?: {
        date?: string;
        ownerEntityId?: string;
        stageAssociation?: string | null;
        privacy?: 'private' | 'shared';
      }
    ) => {
      const { date, ownerEntityId, stageAssociation, privacy } = opts ?? {};
      if (!userId) {
        return;
      }
      // Sequence: AWAIT updateRecord BEFORE firing togglePrivacy. The
      // property/association edit consumes `lastEventSeq` for optimistic
      // concurrency, and the privacy endpoint emits its own events that
      // advance the note's sequence on the backend. Firing them
      // concurrently can lose the race: togglePrivacy lands first,
      // bumps last_event_seq, then updateRecord's stale seq is rejected —
      // surfacing as an intermittent edit failure when both Visibility
      // and any other field change in the same Save.
      const runEdit = async () => {
        try {
          await updateRecordAsync({
            noteEntityId,
            title,
            content,
            date,
            ownerEntityId,
            originalTitle: note.title,
            originalContent: note.content,
            originalDate: note.date,
            originalDatetime: note.datetime,
            originalOwnerEntityId: note.owner_entity_id,
            stageAssociation: stageAssociation ?? null,
            originalStageAssociation: note.stage_entity_id ?? null,
            lastEventSeq: note.last_event_seq,
            userId,
          });
        } catch {
          // updateError surfaces in NoteCard via the editError prop —
          // we still want to attempt the privacy toggle below only if
          // updateRecord succeeded, so bail out here.
          return;
        }

        // Visibility saves through its own endpoint (see useMemoPrivacyToggle),
        // so it only fires when the user actually changed it — awaited, with
        // both outcomes reported, because a fire-and-forget rejection here
        // reads to the user as "I clicked Shared but it stayed Private".
        if (privacy && privacy !== memoPrivacy(note)) {
          await togglePrivacy(noteEntityId, privacy);
        }
      };
      void runEdit();
    },
    [updateRecordAsync, togglePrivacy, userId, note]
  );

  // A projected (not-yet-created) note has no creator or owner association —
  // the server stamps both on execution, and the executing user is the one
  // approving the proposal, i.e. the current viewer. Show them as the
  // prospective creator/owner instead of the literal "Unknown" that the
  // attribution row falls back to. Live records keep the stored values.
  const draft = useRecordDraft();
  const isUnsaved = draft?.isUnsaved ?? false;
  const prospectiveName = useMemo(
    () =>
      isUnsaved && currentMember ? resolveMemberName(currentMember) : undefined,
    [isUnsaved, currentMember]
  );
  const ownerName = useMemo(
    () => getMemoOwnerName(note, orgUsers) ?? prospectiveName,
    [note, orgUsers, prospectiveName]
  );
  const creatorName = useMemo(
    () => getMemoCreatorName(note, orgUsers) ?? prospectiveName,
    [note, orgUsers, prospectiveName]
  );

  // The slideout deliberately first-paints from a *summary* store entry (the
  // list's /query response), which for a note carries neither ahoy_title nor
  // ahoy_content — nothing this surface can render. Honor the store's
  // completeness contract (crmObjectStore.subscribeToStoredObject: sections
  // "should show section-level skeletons until the fetch resolves") instead of
  // painting blank space. Gated on an entry EXISTING but being incomplete:
  //   - no store entry at all → a projected/draft object (the projection is
  //     the truth; there is nothing to wait for), render normally;
  //   - pending optimistic creation → the (incomplete) store entry IS the
  //     record the user just typed, render it immediately;
  //   - unsaved proposal draft → same, the projection is authoritative.
  const { storeObjectData, storeObjectIsComplete } = useStoreObjectSubscription(
    object.entity_id
  );
  const getIsPendingCreation = useCallback(
    () => getPendingCreationsSnapshot().has(object.entity_id),
    [object.entity_id]
  );
  const isPendingCreation = useSyncExternalStore(
    subscribeToPendingCreations,
    getIsPendingCreation
  );
  const isHydrating =
    storeObjectData !== undefined &&
    !storeObjectIsComplete &&
    !isPendingCreation &&
    !isUnsaved;

  if (isHydrating) {
    // Title line + body lines, echoing NoteCard's flat record-slideout layout.
    return (
      <div
        className='flex flex-col gap-3'
        data-testid='note-overview-skeleton'
        aria-hidden='true'
      >
        <Skeleton width='45%' height={20} />
        <SkeletonText lines={4} />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      <NoteCard
        note={note}
        orgId={orgId}
        ownerName={ownerName}
        creatorName={creatorName}
        disableTruncation
        // Record-slideout presentation: flat (no card chrome, hairline
        // divider, single avatar attribution) and click-anywhere-to-edit —
        // the note IS the record here. See NoteCard's `flat` / `editOnClick`.
        flat
        editOnClick
        // Edit mode is driven by the slideout header's "Edit note" button
        // (RecordViewContext owns the state); NoteCard renders the form when true
        // and reports open/close back so the header button toggles correctly.
        isEditMode={editMode}
        onEditModeChange={onEditModeChange}
        // Delete is intentionally NOT wired here — the slideout header's
        // RecordActionMenu (kebab → Delete) is the single delete entry
        // point for note records, matching every other deletable record.
        // NoteCard auto-hides its inline trash when `onDelete` is undefined,
        // so omitting the prop is enough to drop the duplicate affordance.
        // `canDelete` / `handleDelete` stay declared above because the
        // notes-tab uses of NoteCard (contact / company / deal slideouts)
        // still pass them; this file just doesn't.
        users={orgUsers}
        currentUserEntityId={userEntityId}
        onEdit={handleEdit}
        isEditing={isUpdating}
        editError={updateError}
      />
      {/* "Linked records" section — renders nothing when the note has no
          user-visible outbound associations (the section's empty state is
          "don't render the header"). Lives inside this wrapper rather than
          as a sibling in single-slideout.tsx so the note-specific overview
          surfaces stay co-located in one component. */}
      <NoteLinkedRecordsSection object={object} />
    </div>
  );
}
